import { z, ZodIssueCode } from 'zod'
import i18next from 'i18next'
import type { Stageable } from '@/lib/entity/base'
import type { ModifiedField } from './entityFormatter'
import { ClientMutator } from '@/lib/entity/client'
import { ClientConverter } from './entity/client'
import { DepartmentMutator } from '@/lib/entity/department'
import { DepartmentConverter } from './entity/department'
import { PartnerMutator } from '@/lib/entity/partner'
import { PartnerConverter } from './entity/partner'
import { JobTitleMutator } from '@/lib/entity/job_title'
import { JobTitleConverter } from './entity/job_title'
import { JobSkillMutator } from '@/lib/entity/job_skill'
import { JobSkillConverter } from './entity/job_skill'
import { CustomFieldMutator } from '@/lib/entity/custom_field'
import { CustomFieldConverter } from './entity/custom_field'
import { CustomFieldChoiceMutator } from '@/lib/entity/custom_field_choice'
import { CustomFieldChoiceConverter } from './entity/custom_field_choice'
import { HolidayMutator } from '@/lib/entity/holiday'
import { HolidayConverter } from './entity/holiday'
import { RegularHolidayMutator } from '@/lib/entity/regular_holiday'
import { RegularHolidayConverter } from './entity/regular_holiday'
import { AttendanceMutator } from '@/lib/entity/attendance'
import { AttendanceConverter } from './entity/attendance'
import { AssignMutator } from '@/lib/entity/assign'
import { AssignConverter } from './entity/assign'
import { AssignPlanMutator } from '@/lib/entity/assign_plan'
import { AssignPlanConverter } from './entity/assign_plan'
import { VolumedAssignMutator } from '@/lib/entity/volumed_assign'
import { VolumedAssignConverter } from './entity/volumed_assign'
import { VolumedAssignPlanMutator } from '@/lib/entity/volumed_assign_plan'
import { VolumedAssignPlanConverter } from './entity/volumed_assign_plan'
import { UserMutator } from '@/lib/entity/user'
import { UserConverter } from './entity/user'
import { ProjectMutator } from '@/lib/entity/project'
import { ProjectConverter } from './entity/project'
import { EmploymentMutator } from '@/lib/entity/employment'
import { EmploymentConverter } from './entity/employment'
import { WorkOnProjectMutator } from '@/lib/entity/work_on_project'
import { WorkOnProjectConverter } from './entity/work_on_project'
import { TenantMutator } from '@/lib/entity/tenant'
import { TenantConverter } from './entity/tenant'
import { TargetCostOtherMutator } from '@/lib/entity/target_cost_other'
import { TargetCostOtherConverter } from '@/lib/converter/entity/target_cost_other'
import { TargetCostOtherActualMutator } from '@/lib/entity/target_cost_other_actual'
import { TargetCostOtherActualConverter } from '@/lib/converter/entity/target_cost_other_actual'
import { JobTriggerMutator } from '@/lib/entity/job_trigger'
import { JobTriggerConverter } from '@/lib/converter/entity/job_trigger'

type EntityConverter<T> = {
  icon: () => string
  title: () => string
  subTitle: (x: T) => Promise<string>
  modifiedFields: (
    modified: T,
    column: keyof T,
    base: Record<string, any> | undefined
  ) => Promise<ModifiedField> | undefined
}
export interface EntityCard {
  icon: string
  title: string
  subTitle: string
  modifiedFields: Array<string>
  action: string
  isError: boolean
  relationErrorMessages?: string[]
}

const getActionStatus = (isDeleted: boolean, isNewEntity: boolean): string => {
  if (isDeleted) {
    return i18next.t('stages.actions.delete')
  } else if (isNewEntity) {
    return i18next.t('stages.actions.create')
  } else {
    return i18next.t('stages.actions.edit')
  }
}

const convert = async <T extends Stageable>(
  converter: EntityConverter<T['merged']>,
  stage: T
): Promise<EntityCard> => {
  const icon = converter.icon()
  const title = converter.title()
  const subTitle = await converter.subTitle(stage.merged)
  const modifiedFields = (
    await Promise.all(
      Object.keys(stage.modified).map((column) =>
        converter.modifiedFields(stage.modified, column, stage.context)
      )
    )
  ).map((modifiedFields) => {
    if (!modifiedFields) return ''
    const [label, value] = modifiedFields
    return `${label}: ${value}`
  })
  const action = getActionStatus(stage.isDeleted, stage.isNewEntity())
  const isError = await checkError(stage.errors)
  const relationErrorMessages = await getRelationErrorMessages(stage.errors)

  return { icon, title, subTitle, modifiedFields, action, isError, relationErrorMessages }
}

export const convertCardFromEntity = async (stage: Stageable): Promise<EntityCard> => {
  if (stage instanceof ClientMutator) return convert(ClientConverter, stage)
  if (stage instanceof DepartmentMutator) return convert(DepartmentConverter, stage)
  if (stage instanceof PartnerMutator) return convert(PartnerConverter, stage)
  if (stage instanceof JobTitleMutator) return convert(JobTitleConverter, stage)
  if (stage instanceof JobSkillMutator) return convert(JobSkillConverter, stage)
  if (stage instanceof CustomFieldMutator) return convert(CustomFieldConverter, stage)
  if (stage instanceof CustomFieldChoiceMutator) return convert(CustomFieldChoiceConverter, stage)
  if (stage instanceof HolidayMutator) return convert(HolidayConverter, stage)
  if (stage instanceof RegularHolidayMutator) return convert(RegularHolidayConverter, stage)
  if (stage instanceof AttendanceMutator) return convert(AttendanceConverter, stage)
  if (stage instanceof AssignPlanMutator) return convert(AssignPlanConverter, stage)
  if (stage instanceof AssignMutator) return convert(AssignConverter, stage)
  if (stage instanceof VolumedAssignPlanMutator) return convert(VolumedAssignPlanConverter, stage)
  if (stage instanceof VolumedAssignMutator) return convert(VolumedAssignConverter, stage)
  if (stage instanceof UserMutator) return convert(UserConverter, stage)
  if (stage instanceof ProjectMutator) return convert(ProjectConverter, stage)
  if (stage instanceof EmploymentMutator) return convert(EmploymentConverter, stage)
  if (stage instanceof WorkOnProjectMutator) return convert(WorkOnProjectConverter, stage)
  if (stage instanceof TenantMutator) return convert(TenantConverter, stage)
  if (stage instanceof TargetCostOtherMutator) return convert(TargetCostOtherConverter, stage)
  if (stage instanceof TargetCostOtherActualMutator)
    return convert(TargetCostOtherActualConverter, stage)
  if (stage instanceof JobTriggerMutator) return convert(JobTriggerConverter, stage)
  throw new Error('Unknown Stageable entity type.')
}

const checkError = async (errorPromise: Promise<z.ZodError<any> | undefined>): Promise<boolean> => {
  const error = await errorPromise
  return error !== undefined
}

const getRelationErrorMessages = async (
  errorPromise: Promise<z.ZodError<any> | undefined>
): Promise<string[]> => {
  const error = await errorPromise
  if (!error) return []

  return error.issues
    .filter(
      (issue) =>
        issue.code === ZodIssueCode.custom && issue.params?.customCode === 'invalid_relation'
    )
    .map((issue) => issue.message)
}
